/**
 * DocumentsCard
 * -----------------------------------------------------------------------------
 * Super-Admin-only panel that lists the 4 platform documents and lets the SA
 * download each one as a freshly-rendered PDF. The Markdown sources live in
 * /app/memory/*.md on the server and are converted on-the-fly via ReportLab.
 */
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileDown, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DESCRIPTIONS = {
  technical: 'Full architecture, schema, code-level walk-through, hosting & maintenance, risk register.',
  brochure: '1-page sales sheet — give this to prospective workshop owners.',
  msa: 'Master Services Agreement + Terms of Service + one-page Order Form. Legal template.',
  er: 'Entity-Relationship diagram in dbdiagram.io DSL — paste at dbdiagram.io for an interactive view.',
};

export default function DocumentsCard() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/documents`, { credentials: 'include' });
        if (res.ok) {
          const d = await res.json();
          setDocs(d.documents || []);
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const handleDownload = async (slug, title) => {
    setDownloading(slug);
    try {
      const res = await fetch(`${BACKEND_URL}/api/documents/${slug}/pdf`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed to generate ${title}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-z0-9]+/gi, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${title}`);
    } finally { setDownloading(null); }
  };

  return (
    <Card className="mt-8 p-6" data-testid="documents-card">
      <h2 className="font-semibold text-lg mb-1 flex items-center gap-2">
        <FileText className="h-5 w-5 text-teal-600" />
        Platform Documents
      </h2>
      <p className="text-sm text-slate-500 mb-4">
        Generate fresh PDFs from the live Markdown sources. Super Admin only.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {docs.map(d => (
            <div
              key={d.slug}
              className="border border-slate-200 rounded-lg p-3 flex items-start gap-3 hover:border-teal-300 transition-colors"
              data-testid={`doc-${d.slug}`}
            >
              <div className="bg-teal-50 text-teal-700 rounded-lg p-2">
                <FileText className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-900 text-sm">{d.title}</h3>
                <p className="text-xs text-slate-500 mt-1">{DESCRIPTIONS[d.slug] || ''}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={!d.exists || downloading === d.slug}
                onClick={() => handleDownload(d.slug, d.title)}
                data-testid={`doc-download-${d.slug}`}
                className="border-teal-300 text-teal-700 hover:bg-teal-50"
              >
                {downloading === d.slug ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileDown className="h-3.5 w-3.5 mr-1" />
                )}
                {downloading === d.slug ? '' : 'PDF'}
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
